import { useEffect, useRef, useState } from "react";
import { useLocation, useNavigate, useSearchParams } from "react-router-dom";
import { ActionButton, SecondaryAction } from "../components/kit";
import { TourArt, TourRows } from "../components/tour/TourArt";
import { useChromelessOverride } from "../context/ChromelessOverrideContext";
import { homePathForRole } from "../lib/homePath";
import { safeRedirectTarget } from "../lib/redirectTarget";
import { withCreate } from "../lib/activityCreate";
import { coachSetupInviteHref } from "../lib/coachSetup";
import { markTourSeen, TOUR_REPLAY_PARAM } from "../lib/tour";
import { tourFor } from "../lib/tourSteps";
import { track } from "../lib/usageTracker";

/**
 * The guided tour (#1514; D406, D407, D407a), behind `newUi("tour")`: seven cards
 * for a player (`/tour`) and four for a coach (`/tour/coach`), as drawn on the
 * canvas (cards, not the spotlight alternative). Each card has the eyebrow, Skip
 * (not on the last card), the drawing, the heading and lede, the progress dots,
 * Next, and Back from the second card. The last card has one gold action and a
 * text link instead of Next.
 *
 * Chromeless like a step (D390): the screen owns its page through
 * `ChromelessOverrideContext`, the way the log steps do, so there is no ribbon,
 * no top bar and no sidebar. The override is set in a layout effect so the
 * shared header never paints under it. The page column (640 from md, D387)
 * comes from the shell; the card is as wide as that column.
 *
 * The "seen" flag (`lib/tour.js`) is written on Skip and on either action of
 * the last card, and never by a replay (`?replay=1`, from Settings > More).
 * Where it continues to: the original target the set-up handed over in router
 * state (`state.next`, checked by `safeRedirectTarget`), else the role's home;
 * a replay's Skip goes back to Settings. One gold element per screen (R-003):
 * only the last card's primary action; Next is navy. The last card's text link is
 * `tour-home`: it is the card's way home (the navigation guard reads a control whose
 * id ends `-home` as the labelled exit of a step, G2).
 */
export default function Tour({ tourRole: role = "player" }) {
  const tour = tourFor(role);
  const steps = tour.steps;
  const nav = useNavigate();
  const location = useLocation();
  const [search] = useSearchParams();
  const replay = search.get(TOUR_REPLAY_PARAM) === "1";
  const home = homePathForRole(role);
  const target = replay ? home : safeRedirectTarget(location.state?.next, home);

  const [index, setIndex] = useState(0);
  const step = steps[index];
  const total = steps.length;

  const { setActive: setChromeless } = useChromelessOverride();
  useEffect(() => {
    setChromeless(true);
    return () => setChromeless(false);
  }, [setChromeless]);

  // A new card announces itself: focus moves to its heading (not on first paint).
  const titleRef = useRef(null);
  const firstPaint = useRef(true);
  useEffect(() => {
    if (firstPaint.current) {
      firstPaint.current = false;
      return;
    }
    titleRef.current?.focus();
  }, [index]);

  const finishTo = (path) => {
    if (!replay) markTourSeen(role);
    nav(path, { replace: true });
  };
  const skip = () => {
    if (!replay) track("tour.skipped"); // a replay from Settings is not the first-run tour
    finishTo(replay ? "/settings" : target);
  };
  const primary = () => {
    if (!replay) track("tour.done");
    finishTo(role === "coach" ? coachSetupInviteHref() : withCreate("/performance"));
  };

  const eyebrow = step.kind === "intro" ? step.eyebrow : `Step ${index + 1} of ${total}`;
  const linkStyle = {
    justifyContent: "center",
    marginBlock: 0,
    fontSize: 15,
    "--nw-tap-lh": 1.5,
  };

  return (
    <div
      data-testid="tour-page"
      data-role={role}
      data-step={index + 1}
      style={{
        boxSizing: "border-box",
        minHeight: "100dvh",
        display: "flex",
        flexDirection: "column",
        padding: "24px var(--nw-space-page) var(--nw-space-page)",
        color: "var(--nw-ink)",
      }}
    >
      <span data-testid="tour-new-screen" className="sr-only" aria-hidden="true" />

      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", height: 44 }}>
        <span className="nw-eyebrow" data-testid="tour-eyebrow" style={{ fontSize: 12, letterSpacing: "0.08em" }}>
          {eyebrow}
        </span>
        {step.last ? (
          <span aria-hidden="true" style={{ width: 44, height: 44 }} />
        ) : (
          <SecondaryAction
            variant="link"
            onClick={skip}
            data-testid="tour-skip"
            style={{ ...linkStyle, justifyContent: "flex-end", padding: "0 8px" }}
          >
            {step.skip || "Skip"}
          </SecondaryAction>
        )}
      </div>

      {step.kind === "screen" && <TourArt art={step.art} />}

      <div style={{ marginTop: step.kind === "intro" ? 28 : 24, display: "flex", flexDirection: "column", gap: 12 }}>
        <h1
          ref={titleRef}
          tabIndex={-1}
          data-testid="tour-title"
          style={{
            margin: 0,
            fontFamily: "'Manrope', 'DM Sans', sans-serif",
            fontWeight: 800,
            fontSize: step.kind === "intro" ? 30 : 28,
            lineHeight: 1.15,
            letterSpacing: "-0.01em",
            color: "var(--nw-ink)",
            outline: "none",
          }}
        >
          {step.title}
        </h1>
        <p data-testid="tour-lede" style={{ margin: 0, fontSize: 17, lineHeight: 1.5, color: "var(--nw-ink-lede)" }}>
          {step.lede}
        </p>
        {step.aside && (
          <p data-testid="tour-aside" style={{ margin: 0, fontSize: 15, lineHeight: 1.5, color: "var(--nw-ink-muted)" }}>
            {step.aside}
          </p>
        )}
      </div>

      {step.kind === "intro" && <TourRows rows={step.rows} />}

      <div style={{ flex: "1 1 auto" }} />

      <div
        role="img"
        aria-label={`Step ${index + 1} of ${total}`}
        data-testid="tour-dots"
        style={{ display: "flex", justifyContent: "center", gap: 8, margin: "20px 0" }}
      >
        {steps.map((_, i) => (
          <span
            key={i}
            style={{
              width: i === index ? 20 : 8,
              height: 8,
              borderRadius: "var(--nw-radius-pill)",
              background: i === index ? "var(--nw-navy)" : "var(--nw-line-strong)",
            }}
          />
        ))}
      </div>

      {step.last ? (
        <>
          <ActionButton onClick={primary} data-testid="tour-primary">
            {step.primary}
          </ActionButton>
          <SecondaryAction
            variant="link"
            onClick={() => finishTo(target)}
            data-testid="tour-home"
            style={{ ...linkStyle, marginTop: 8, width: "100%" }}
          >
            {step.secondary}
          </SecondaryAction>
        </>
      ) : (
        <>
          <ActionButton
            onClick={() => setIndex(index + 1)}
            data-testid="tour-next"
            data-tone="navy"
            style={{ background: "var(--nw-navy)", color: "#FFFFFF" }}
          >
            {step.next || "Next"}
          </ActionButton>
          {index > 0 ? (
            <SecondaryAction
              variant="link"
              onClick={() => setIndex(index - 1)}
              data-testid="tour-back"
              style={{ ...linkStyle, marginTop: 8, width: "100%" }}
            >
              Back
            </SecondaryAction>
          ) : (
            step.note && (
              <div
                data-testid="tour-note"
                style={{ marginTop: 12, textAlign: "center", fontSize: 13, lineHeight: 1.4, color: "var(--nw-ink-muted)" }}
              >
                {step.note}
              </div>
            )
          )}
        </>
      )}
    </div>
  );
}
